import { zValidName } from '@daymade/common-lib/validationRules';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { successSchema } from '../utils';
import {
  ZErrorResponseSchema,
  ZLoginUserSchema,
  ZProfileResponseSchema,
  ZRegisterUserSchema,
  ZRequestPasswordReset,
  ZUsePasswordReset,
} from './models';

export * from './models';

const c = initContract();

export const publicAuthContract = c.router(
  {
    register: {
      method: 'POST',
      path: '/register',
      body: ZRegisterUserSchema,
      responses: {
        200: z.object({
          player: z.object({
            id: z.string(),
            emailAddress: z.string(),
            firstName: z.string(),
            lastName: z.string(),
            phoneNumber: z.string().nullable(),
            referralId: z.string(),
            status: z.string(),
            role: z.string(),
          }),
          token: z.string(),
        }),
        400: ZErrorResponseSchema,
        409: c.type<{
          message: 'Email address already registered.';
          reason?: 'EMAIL_ADDRESS_ALREADY_REGISTERED';
        }>(),
        422: c.type<{ message: string; reason?: 'EMAIL_ADDRESS_NOT_VALID' }>(),
      },
      summary:
        'Register a player with email, first name, last name and password',
    },
    requestPasswordReset: {
      method: 'POST',

      path: '/send-password-reset-token',
      body: ZRequestPasswordReset,
      responses: {
        200: successSchema,
        400: c.type<{
          reason:
            | 'ACCOUNT_NOT_FOUND'
            | 'ACCOUNT_CLOSED'
            | 'ACCOUNT_FROZEN'
            | 'PASSWORD_RESET_NOT_AVAILABLE';
        }>(),
      },
    },
    usePasswordResetToken: {
      method: 'POST',

      path: 'use-password-reset-token',
      body: ZUsePasswordReset,
      responses: {
        200: z.object({ success: z.boolean(), jwtToken: z.string() }),
        400: c.type<{
          reason: 'TOKEN_NOT_FOUND' | 'TOKEN_EXPIRED' | 'TOKEN_ALREADY_USED';
        }>(),
      },
    },
    login: {
      method: 'POST',
      path: '/login',
      body: ZLoginUserSchema,
      responses: {
        200: z.object({
          player: ZProfileResponseSchema,
          token: z.string(),
        }),
        401: z.object({
          message: z.string(),
        }),
      },
      summary: 'Login a player with email and password',
    },
    loginWithGoogle: {
      method: 'POST',
      path: '/login/google',
      body: z.object({
        accessToken: z.string(),
      }),
      responses: {
        200: z.object({
          token: z.string(),
          welcomeName: z.string(),
          abtestVariants: z
            .record(z.string(), z.string())
            .optional()
            .nullable(),
        }),
        401: ZErrorResponseSchema,
      },
    },
    loginWithFacebook: {
      method: 'POST',
      path: '/login/facebook',
      body: z.object({
        accessToken: z.string(),
      }),
      responses: {
        200: z.object({
          token: z.string(),
          welcomeName: z.string(),
          abtestVariants: z
            .record(z.string(), z.string())
            .optional()
            .nullable(),
        }),
        401: ZErrorResponseSchema,
      },
    },
    loginWithApple: {
      method: 'POST',
      path: '/login/apple',
      body: z.object({
        accessToken: z.string(),
      }),
      responses: {
        200: z.object({
          token: z.string(),
          welcomeName: z.string(),
          abtestVariants: z
            .record(z.string(), z.string())
            .optional()
            .nullable(),
        }),
        401: ZErrorResponseSchema,
      },
    },
    verifyOperator: {
      method: 'GET',
      path: '/verifyoperator',
      responses: {
        200: z.object({
          isOperator: z.boolean(),
        }),
      },
      summary: 'Returns true if the player is an operator',
    },
    validateGoogleAccessToken: {
      method: 'POST',
      path: '/validate/google',
      body: z.object({
        accessToken: z.string(),
      }),
      responses: {
        200: z.object({
          token: z.string(),
          welcomeName: z.string(),
          abtestVariants: z
            .record(z.string(), z.string())
            .optional()
            .nullable(),
        }),
        201: z.object({
          googleUserId: z.string(),
        }),
        400: ZErrorResponseSchema,
      },
      summary: 'Register a player with Google access token',
    },
    validateFacebookAccessToken: {
      method: 'POST',
      path: '/validate/facebook',
      body: z.object({
        accessToken: z.string(),
        userId: z.string(),
        email: z.string(),
      }),
      responses: {
        200: z.object({
          token: z.string(),
          welcomeName: z.string(),
          abtestVariants: z
            .record(z.string(), z.string())
            .optional()
            .nullable(),
        }),
        201: z.object({
          facebookUserId: z.string(),
        }),
        400: ZErrorResponseSchema,
      },
      summary: 'Register a player with Facebook access token',
    },
    validateAppleAccessToken: {
      method: 'POST',
      path: '/validate/apple',
      body: z.object({
        accessToken: z.string(),
      }),
      responses: {
        200: z.object({
          token: z.string(),
          welcomeName: z.string(),
          abtestVariants: z
            .record(z.string(), z.string())
            .optional()
            .nullable(),
        }),
        201: z.object({
          appleUserId: z.string(),
          appleEmailAddress: z.string(),
        }),
        400: ZErrorResponseSchema,
      },
      summary: 'Register a player with Apple access token',
    },
    getFacebookProfileFromCode: {
      method: 'POST',
      path: '/profile/facebook',
      body: z.object({
        code: z.string(),
        redirect_uri: z.string(),
      }),
      responses: {
        200: z.object({
          accessToken: z.string(),
          id: z.string(),
          email: z.string(),
          firstName: z.string(),
          lastName: z.string(),
        }),
        400: ZErrorResponseSchema,
      },
      summary:
        'Retrieves Facebook profile information from a Facebook auth code',
    },
    country: {
      method: 'GET',
      path: '/country',
      responses: {
        200: z.object({
          country: z.string(),
        }),
      },
      summary: 'Returns a player object from the extracted JWT token',
    },
    currentTime: {
      method: 'GET',
      path: '/current-time',
      responses: {
        200: z.object({
          now: z.coerce.date(),
          reflectedHeaders: z.string().optional(),
        }),
      },
      summary:
        'Returns the current time, without any additional caching headers',
    },
    currentTimeCached: {
      method: 'GET',
      path: '/current-time-cached',
      responses: {
        200: z.object({
          now: z.coerce.date(),
        }),
      },
      summary:
        'Returns the current time, with any additional caching headers to test Cloudfront distribution is behaving',
    },
    signUpForNewsletterWithDiscount: {
      method: 'POST',
      path: '/sign-up-for-newsletter-with-discount',
      body: z.object({
        source: z.string(),
        emailAddress: z.string().email(),
        firstName: zValidName.optional(),
        utmParams: z.record(z.string()).nullable().optional(),
      }),
      responses: {
        200: successSchema,
        400: ZErrorResponseSchema,
      },
      summary: 'Sign up for the newsletter with a discount code',
    },
  },
  {
    pathPrefix: '/public',
  },
);
